import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { yoyoData } from './yoyoData';
import { useCart } from './CartContext';
import { trackAnalyticsEvent } from '../utils/analytics';
import './YoYoPage.css';
import { FaArrowLeft } from 'react-icons/fa';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import getApiBase from '../utils/apiBase';
import Seo, { getSiteUrl } from '../components/Seo';
import { useI18n } from '../i18n/I18nContext';
import { getCatalogCopy, getCatalogFaqs } from './catalogCopy';
import { getLocalizedPath } from '../i18n/localizedRoutes';
import { getProductTags } from './productFilters';
import { findProductByRouteParam, getProductPath } from '../utils/productSlug';

const API_BASE = getApiBase();

const getInitialProduct = (catalog, productSlug) => {
  const publicProducts = catalog.filter((product) => Number(product.id) !== 7);
  const product = findProductByRouteParam(publicProducts, productSlug);
  return {
    productSlug,
    product,
    canonicalPath: product ? product.path || getProductPath(product, publicProducts) : '',
    relatedProducts: product ? publicProducts.filter((item) => item.id !== product.id).slice(0, 3) : [],
  };
};

const YoYoPage = ({ initialCatalog = yoyoData }) => {
  const { productSlug } = useParams();
  const initialProduct = useMemo(() => getInitialProduct(initialCatalog, productSlug), [initialCatalog, productSlug]);
  const [loadedProduct, setLoadedProduct] = useState(null);
  const activeProduct = loadedProduct?.productSlug === productSlug ? loadedProduct : initialProduct;
  const yoyo = activeProduct.product;
  const relatedProducts = activeProduct.relatedProducts;
  const canonicalProductPath = activeProduct.canonicalPath;
  const loading = loadedProduct?.productSlug !== productSlug && !yoyo;
  const { dispatch } = useCart();
  const navigate = useNavigate();
  const { t, formatNumber, locale } = useI18n();
  const [selection, setSelection] = useState({ productId: null, color: '', quantity: 1 });

  useEffect(() => {
    let isMounted = true;
    const acceptProduct = (result) => {
      if (!isMounted) return;
      setLoadedProduct({ ...result, productSlug });
      if (result.product && result.canonicalPath && result.canonicalPath !== `/store/${productSlug}`) {
        navigate(getLocalizedPath(result.canonicalPath, locale), { replace: true });
      }
    };
    const loadProduct = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/products/${encodeURIComponent(productSlug || "")}`);
        if (response.status === 404 || response.status === 410) {
          acceptProduct({ product: null, canonicalPath: '', relatedProducts: [] });
          return;
        }
        if (!response.ok) throw new Error('PRODUCT_FETCH_FAILED');
        const data = await response.json();
        const product = Number(data.product?.id) === 7 ? null : data.product || null;
        acceptProduct({
          product,
          canonicalPath: product ? data.canonicalPath || product.path || getProductPath(product, initialCatalog) : '',
          relatedProducts: Array.isArray(data.relatedProducts)
            ? data.relatedProducts.filter((item) => Number(item.id) !== 7) : [],
        });
      } catch {
        acceptProduct(getInitialProduct(initialCatalog, productSlug));
      }
    };
    loadProduct();
    return () => {
      isMounted = false;
    };
  }, [navigate, productSlug, initialCatalog, locale]);

  const colors = useMemo(() => (
    yoyo?.colors?.length
      ? yoyo.colors
      : yoyo
        ? [{ name: "Default", imageUrl: yoyo.imageUrl, price: yoyo.price }]
        : []
  ), [yoyo]);

  const selectedColorDetails = (selection.productId === yoyo?.id
    ? colors.find((color) => color.name === selection.color) : null) || colors[0];
  const selectedColor = selectedColorDetails?.name || '';
  const quantity = selection.productId === yoyo?.id ? selection.quantity : 1;
  const currentImage = selectedColorDetails?.imageUrl || yoyo?.imageUrl;
  const currentPrice = Number(selectedColorDetails?.price ?? yoyo?.price ?? 0);
  const formatPrice = (value) => formatNumber(value, { style: 'currency', currency: 'USD' });
  const copy = getCatalogCopy(yoyo, t);
  const faqs = getCatalogFaqs(yoyo, copy, t, formatPrice, currentPrice);
  const displayedColor = selectedColor === 'Default' ? t('product.defaultColor') : selectedColor;
  const updateSelection = (changes) => setSelection({ productId: yoyo.id, color: selectedColor, quantity, ...changes });

  const addItemToCart = () => {
    const count = Number(quantity);
    if (!yoyo || !Number.isInteger(count) || count < 1 || count > 9999) return;
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        ...yoyo,
        quantity: count,
        selectedColor,
        imageUrl: currentImage, // Save the selected color image in the cart
        price: currentPrice, // Save the price based on the selected color
      },
    });
  };

  if (loading) {
    return (
      <div className="yoyo-page">
        <Seo
          title={t('product.loadingTitle')}
          description={t('product.loadingDescription')}
          path="/store"
          noindex
        />
        <p>{t('product.loading')}</p>
      </div>
    );
  }

  if (!yoyo) {
    return (
      <div className="yoyo-page">
        <Seo
          title={t('product.notFoundTitle')}
          description={t('product.notFoundDescription')}
          path={`/store/${productSlug || ''}`}
          noindex
        />
        <button onClick={() => navigate(-1)} className="back-button">
          <FaArrowLeft /> {t('product.back')}
        </button>
        <p>{t('product.notFound')}</p>
      </div>
    );
  }

  const siteUrl = getSiteUrl();
  const productPath = getLocalizedPath(canonicalProductPath || yoyo.path || getProductPath(yoyo, [yoyo, ...relatedProducts]), locale);
  const imageSource = resolveImageUrl(currentImage || yoyo.imageUrl);
  const imageUrl = imageSource.startsWith('http')
    ? imageSource
    : `${siteUrl}${imageSource.startsWith('/') ? imageSource : `/${imageSource}`}`;
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: yoyo.name,
    description: copy.description,
    image: [imageUrl],
    sku: String(yoyo.id),
    brand: {
      '@type': 'Brand',
      name: getProductTags(yoyo).brand?.[0] || 'YCH-YOYO',
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: Number(currentPrice).toFixed(2),
      url: `${siteUrl}${productPath}`,
    },
  };
  const faqSchema = faqs.length
    ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    }
    : null;
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('nav.home'),
        item: `${siteUrl}${getLocalizedPath('/', locale)}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('nav.store'),
        item: `${siteUrl}${getLocalizedPath('/store', locale)}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: yoyo.name,
        item: `${siteUrl}${productPath}`,
      },
    ],
  };

  return (
    <div className="yoyo-page">
      <Seo
        title={copy.title}
        description={copy.description}
        path={productPath}
        image={currentImage || yoyo.imageUrl}
        type="product"
        structuredData={[productSchema, faqSchema, breadcrumbSchema]}
      />
      <nav className="breadcrumb-nav" aria-label={t('product.breadcrumb')}>
        <Link to={getLocalizedPath('/', locale)}>{t('nav.home')}</Link> / <Link to={getLocalizedPath('/store', locale)}>{t('nav.store')}</Link> / <span>{yoyo.name}</span>
      </nav>
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> {t('product.back')}
      </button>
      <div className="yoyo-content">
        {/* Main Yo-Yo Image */}
        <div className="main-image">
          <img
            src={resolveImageUrl(currentImage)}
            alt={t('product.mainImageAlt', { product: yoyo.name, color: displayedColor })}
          />
        </div>
        {/* Yo-Yo Details */}
        <div className="yoyo-details">
          <h1>{yoyo.name}</h1>
          <p>{t('product.color', { color: displayedColor })}</p>
          <p>{t('common.price', { price: formatPrice(Number(currentPrice)) })}</p>
          {/* Color Selection */}
          <div className="color-selection">
            <div className="color-options">
              {colors.map(color => (
                <img
                  key={color.name}
                  src={resolveImageUrl(color.imageUrl)}
                  alt={color.name}
                  className={`color-thumbnail ${color.name === selectedColor ? 'selected' : ''}`}
                  onClick={() => {
                    updateSelection({ color: color.name });
                    trackAnalyticsEvent('click', { target: 'product.color_select', productId: yoyo.id });
                  }}
                />
              ))}
            </div>
          </div>

          {/* Quantity Selection */}
          <div className="quantity-selection">
            <label htmlFor="quantity">{t('product.quantity')} </label>
            <input
              type="number"
              id="quantity"
              value={quantity}
              onChange={(e) => updateSelection({ quantity: e.target.value })}
              min="1"
              max="9999"
              step="1"
            />
          </div>
          <button data-analytics-target="product.add_to_cart" className="add-to-cart-btn" onClick={addItemToCart}>{t('product.addToCart')}</button>


          {/* Includes*/}
          <h3>{t('product.includes')}</h3>
          <p>{copy.details}</p>
          {/* Description */}
          <h3>{t('product.description')}</h3>
          <p>{copy.description}</p>
          {copy.collection && (
            <p><Link to={getLocalizedPath(copy.collection.path, locale)}>{t(`catalog.category.${copy.collection.category}`)}</Link></p>
          )}
          {Number(yoyo.id) === 6 && copy.collection && (
            <p><Link to={getLocalizedPath('/team/zirui-qiu', locale)}>{t('catalog.profileLink')}</Link></p>
          )}
          {/* Conditionally display the video */}
          {yoyo.videoUrl && (
            <div className="yoyo-video">
              <iframe
                title={t('product.demoVideo', { name: yoyo.name })}
                src={yoyo.videoUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          )}

          {faqs.length > 0 && (
            <section className="product-faqs" aria-label={t('product.faqAria', { name: yoyo.name })}>
              <h3>{t('product.faq')}</h3>
              {faqs.map((item) => (
                <div key={item.question} className="product-faq-item">
                  <h4>{item.question}</h4>
                  <p>{item.answer}</p>
                </div>
              ))}
            </section>
          )}

          {relatedProducts.length > 0 && (
            <section className="related-products" aria-label={t('product.relatedAria')}>
              <h3>{t('product.related')}</h3>
              <ul>
                {relatedProducts.map((product) => (
                  <li key={product.id}>
                    <Link to={getLocalizedPath(product.path || getProductPath(product, [yoyo, ...relatedProducts]), locale)}>
                      {product.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default YoYoPage;
